/**
 * Shared DOM-application logic for the B2B/business-context feature.
 *
 * This file is intentionally loaded as a CLASSIC script (no `type="module"`
 * on its <script> tag), not an ES module. Module scripts are always deferred
 * until the document has finished parsing, which is too late to safely swap
 * PC menu / drawer content before header-menu.js's `connectedCallback` runs
 * (see docs for the full rationale). A classic script, in contrast, runs
 * immediately wherever it appears, strictly before any deferred/module
 * script on the page.
 *
 * Responsibility boundary (do not blur this):
 *  - This file NEVER re-implements the business-context judgement logic
 *    (customer tags / request.path / query parameter). That logic lives in
 *    exactly two places: snippets/business-context.liquid (server-side) and
 *    the small inline <script> in layout/theme.liquid's <head> (client-side,
 *    query-parameter resolution only). This file only READS the result via
 *    `document.documentElement.dataset.businessContext` and applies it to
 *    the DOM.
 *
 * DOM contract this file expects sections/blocks to follow:
 *  - Simple attribute/class toggles (no custom-element timing concerns):
 *      - `[data-business-logo-pending]` <img> elements have `data-src`
 *        promoted to `src` when active (and their `[data-business-logo-slot]`
 *        ancestor pairs are shown/hidden accordingly).
 *  - Content swaps for interactive components, only present when the server
 *    could not resolve business-context on its own ("unknown" state):
 *      - `[data-business-slot="NAME"]` marks the live container whose
 *        children should be replaced.
 *      - `<template data-business-template="NAME">` (a sibling of the slot)
 *        holds the business-variant markup to clone in.
 *      - Once resolved (business or not), the `<template>` is removed either
 *        way so this only ever runs once per slot.
 *  - `header-layout` is a slot like any other, but its swapped-in content
 *    (snippets/header-layout-business.liquid) itself contains its own
 *    nested `header-menu-desktop`/`header-menu-drawer` slot+template pairs
 *    (produced independently by blocks/_header-menu.liquid, unchanged).
 *    Those nested templates don't exist in the DOM until after the
 *    `header-layout` swap runs, so its post-swap hook re-invokes
 *    `applySlotTemplates()` to resolve them in the same pass.
 */
(function () {
  'use strict';

  /** @returns {boolean} */
  function isBusinessActive() {
    return document.documentElement.dataset.businessContext === 'true';
  }

  /**
   * Post-swap hooks per named slot. Each interactive component has its own
   * one-off follow-up (re-binding listeners, resetting slide state, etc.).
   * Keyed by the `data-business-slot`/`data-business-template` name.
   */
  const POST_SWAP_HOOKS = {
    'header-menu-desktop': (slot) => {
      const headerMenu = slot.closest('header-menu');
      if (headerMenu && typeof headerMenu.refreshItems === 'function') {
        headerMenu.refreshItems();
      }
    },
    announcement: (slot) => {
      const bar = slot.closest('announcement-bar-component');
      if (bar) bar.current = 0;
    },
    'footer-menu': () => {
      // The footer's own accordion script (jQuery, footer.liquid) already
      // re-binds idempotently whenever it runs; a resize event is the
      // lightest way to re-trigger it without adding a second script.
      window.dispatchEvent(new Event('resize'));
    },
    'header-layout': (slot) => {
      // The business layout just cloned in (blocks/_header-logo.liquid /
      // blocks/_header-menu.liquid, unchanged) independently re-evaluates
      // business-context and, for its own "unknown" case, nests its own
      // logo ([data-business-logo-slot]) and menu ([data-business-slot=
      // "header-menu-desktop"/"header-menu-drawer"]) slots + <template> pairs
      // inside the markup we just inserted. Those didn't exist in the DOM when
      // the outer pass started, so resolve them now.
      //
      // IMPORTANT: scope BOTH passes to the just-swapped subtree (`slot`).
      // The normal header stays in the DOM (only CSS-hidden) while business is
      // active, and it still contains DUPLICATE [data-business-slot] /
      // [data-business-logo-slot] elements. A document-wide query would match
      // those hidden duplicates first and leave the VISIBLE business header
      // showing the normal (wrong) menu and the normal (dark, invisible) logo.
      applyLogo(slot);
      applySlotTemplates(slot);

      // Then REMOVE the normal (non-business) header from the DOM entirely —
      // CSS-hiding it is not enough. The normal header keeps live, duplicated
      // fixed IDs and custom elements — notably the drawer's
      // <details id="Details-menu-drawer-container"> / <header-drawer> and the
      // search trigger. With two of each in the DOM, the business header's
      // hamburger and search break. Server-confirmed business pages never render
      // the normal header at all (and work), so removing it here reproduces that
      // clean single-header state.
      const headerComponent = slot.closest('#header-component');
      if (headerComponent) {
        headerComponent
          .querySelectorAll(':scope > .header__row:not(.header-business), :scope > .header-policy')
          .forEach((el) => el.remove());
      }
    },
  };

  function applySlotTemplates(root) {
    const scope = root || document;
    scope.querySelectorAll('template[data-business-template]').forEach((tpl) => {
      if (!(tpl instanceof HTMLTemplateElement)) return;

      const name = tpl.getAttribute('data-business-template');
      const slot = scope.querySelector(`[data-business-slot="${name}"]`);

      if (slot && isBusinessActive()) {
        slot.replaceChildren(tpl.content.cloneNode(true));
        const hook = name ? POST_SWAP_HOOKS[name] : undefined;
        if (hook) hook(slot);
      }

      // Resolved either way (business or not) - no longer needed.
      tpl.remove();
    });
  }

  function applyLogo(root) {
    const scope = root || document;
    scope.querySelectorAll('[data-business-logo-slot="business"]').forEach((slot) => {
      const original = scope.querySelector('[data-business-logo-slot="original"]');

      if (isBusinessActive()) {
        const img = slot.querySelector('img[data-business-logo-pending]');
        if (img) {
          const src = img.getAttribute('data-src');
          if (src) img.setAttribute('src', src);
          img.removeAttribute('data-business-logo-pending');
        }
        slot.hidden = false;
        slot.removeAttribute('inert');
        if (original) original.hidden = true;

        // 法人確定時はロゴのリンク先を法人トップ(/pages/business)へ差し替える。
        // unknownページはサーバー側で href=root_url のまま出力されているため、
        // ここで data-business-href の値へ更新する（サーバー確定ページは最初から
        // /pages/business のため data-business-href を持たず、この分岐は何もしない）。
        const logoLink = slot.closest('a[data-business-href]');
        if (logoLink) {
          logoLink.setAttribute('href', logoLink.getAttribute('data-business-href'));
        }
      } else {
        slot.remove();
      }
    });
  }

  /**
   * Applies the resolved business-context state to the DOM. Idempotent and
   * safe to call any number of times (already-applied nodes are no-ops on
   * subsequent calls), so it can run:
   *   1. Immediately, inline, right after the relevant markup exists.
   *   2. Again on `shopify:section:load`, to stay in sync with Theme Editor
   *      section reloads.
   */
  function applyBusinessState() {
    applyLogo();
    applySlotTemplates();
  }

  document.addEventListener('shopify:section:load', applyBusinessState);

  // Safety net only. Each section (header/announcement-bar/footer) already
  // calls `applyBusinessState()` inline right after its own markup so the
  // swap happens as early as possible (minimizing flicker); this call just
  // catches anything missed (idempotent - already-applied nodes are no-ops).
  document.addEventListener('DOMContentLoaded', applyBusinessState);

  window.BusinessContext = { applyBusinessState, isBusinessActive };
})();
